/**
 * Enhanced ClassDashboard using the new component system
 * Demonstrates comprehensive class management with analytics, progress tracking, and advanced features
 */

import React, { useEffect, useState } from 'react';
import {
  FiBarChart,
  FiBookOpen,
  FiClock,
  FiEdit,
  FiEye,
  FiRotateCcw,
  FiTarget,
  FiSearch,
  FiPlus,
  FiUsers,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { TABLE_NAMES } from '@grcs/utils/database/types';

// New component system imports
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Stack,
  useColorModeValue,
  HStack,
  Progress,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  AdminLayout,
  Card,
  DataTable,
  Breadcrumb,
  TableColumn,
} from '@grcs/components';

// Existing hooks and types
import {
  useClassOperations,
  useHomeworkOperations,
  useStudentOperations,
  useUserOperations,
} from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import {
  ArticleProfile,
  ClassProfile,
  HomeworkStudentProfile,
  HomeworkV2Profile,
  StudentProfile,
} from '@grcs/types/DataTypes';
import { UserRole } from '@grcs/types/UserRole';
import { parseQuestions } from '@grcs/utils/content/parseQuestions';
import { UnpublishModal } from '../shared/components/modals/UnpublishModal';

interface ClassDashboardProps {
  from?: string;
}

interface ClassStats {
  totalStudents: number;
  totalHomeworks: number;
  averageCompletion: number;
  activeHomeworks: number;
  className: string;
  teacherName: string;
  semesterYear: string;
  grade: string;
}

interface EnhancedHomework extends HomeworkV2Profile {
  completion_rate?: number;
  average_score?: number;
  submissions_count?: number;
  status?: 'active' | 'draft' | 'archived';
  word_count?: number;
  due_date?: string;
  content_id?: string;
  homework_id?: string;
}

interface ClassStudent extends StudentProfile {
  completion_rate?: number;
  average_score?: number;
  assignments_completed?: number;
  last_activity?: string;
}

// Helper function to calculate score from question_attempted JSON
const calculateScore = (
  questionAttempted: string | null,
  articleQuestions: string | null,
): number => {
  if (!questionAttempted || !articleQuestions) return 0;

  try {
    // Parse the question_attempted data (Map<questionIndex, isCorrect>)
    const attempted = JSON.parse(questionAttempted);
    const questions = parseQuestions(articleQuestions);
    const totalQuestions = questions.length;

    if (totalQuestions === 0) return 0;

    // Count correct answers
    const correctAnswers = Object.values(attempted).filter(
      (isCorrect) => isCorrect === true,
    ).length;

    return Math.round((correctAnswers / totalQuestions) * 100);
  } catch (error) {
    console.error('Error calculating score:', error);
    return 0;
  }
};

export const ClassDashboardEnhanced: React.FC<ClassDashboardProps> = ({
  from,
}) => {
  const { classId, year } = useParams();
  const classOps = useClassOperations();
  const homeworkOps = useHomeworkOperations();
  const studentOps = useStudentOperations();
  const userOps = useUserOperations();
  const navigate = useNavigate();

  const [homeworks, setHomeworks] = useState<EnhancedHomework[]>([]);
  const [students, setStudents] = useState<ClassStudent[]>([]);
  const [, setClassInfo] = useState<ClassProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(from === 'analytics' ? 2 : 0);
  const [assignmentQuery, setAssignmentQuery] = useState('');
  const [studentQuery, setStudentQuery] = useState('');
  const surface = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const subtle = useColorModeValue('gray.50', 'gray.700');
  const [stats, setStats] = useState<ClassStats>({
    totalStudents: 0,
    totalHomeworks: 0,
    averageCompletion: 0,
    activeHomeworks: 0,
    className: '',
    teacherName: '',
    semesterYear: '',
    grade: '',
  });

  const [{ role }] = useLocalStorage('user', {});
  const isTeacherView =
    (from === 'home' || from === 'analytics') && role === UserRole.TEACHER;
  const isAdminView =
    (from === 'home' || from === 'analytics') && role === UserRole.ADMIN;
  const [isRecallOpen, setIsRecallOpen] = useState(false);
  const [recallHomework, setRecallHomework] = useState<EnhancedHomework>();
  const onRecallClick = () => {
    handleRecall();
    setIsRecallOpen(false);
  };
  const handleRecall = async () => {
    if (recallHomework) {
      try {
        // Step 1: Remove from student table
        await userOps.supabaseClient
          .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
          .delete()
          .eq('homework_token', recallHomework.homework_token)
          .eq('class_token', classId);
        // Step 2: Mark as deleted in homework table
        await userOps.supabaseClient
          .from(TABLE_NAMES.HOMEWORK)
          .update({ deleted: true })
          .eq('homework_token', recallHomework.homework_token)
          .eq('class_token', classId);
        // Step 3: Toast and refresh
        const { data } = await homeworkOps.getHomeworkByClassId(classId!);
        setHomeworks?.((data as unknown as HomeworkV2Profile[]) || []);
      } catch (error) {
        console.error('Error recall homework:', error);
      }
    }
  };

  // Load class data
  useEffect(() => {
    const loadClassData = async () => {
      if (!classId) return;

      try {
        setIsLoading(true);

        // Load class information
        const { data: classData } = await classOps.getClassInfoByClassId(
          classId,
        );
        if (classData && classData.length > 0) {
          const classProfile = classData[0] as ClassProfile;
          setClassInfo(classProfile);
          setStats((prev) => ({
            ...prev,
            className: classProfile.class_name,
            semesterYear: classProfile.semester_year,
            grade: classProfile.grade,
            teacherName: 'Unassigned',
          }));

          // Load teacher name
          if (classProfile.teacher_token) {
            const { data: teacherData } =
              await userOps.getTeacherNameByTeacherToken(
                classProfile.teacher_token,
              );
            const teacherName =
              teacherData?.[0]?.full_name || 'Unknown Teacher';

            setStats((prev) => ({
              ...prev,
              className: classProfile.class_name,
              teacherName,
              semesterYear: classProfile.semester_year,
              grade: classProfile.grade,
            }));
          }
        }

        // Load homeworks
        const { data: homeworkData } = await homeworkOps.getHomeworkByClassId(
          classId,
        );

        // Load ALL homework-student relations for this class (for metrics calculation)
        const { data: allSubmissions } =
          await homeworkOps.getAllHomeworksByClassId(classId);

        // Get unique article tokens to fetch article data
        const articleTokens = Array.from(
          new Set(
            ((allSubmissions as HomeworkStudentProfile[]) || []).map(
              (s) => s.article_token,
            ),
          ),
        );

        // Fetch all articles for question data
        const articlesMap = new Map<string, ArticleProfile>();
        if (articleTokens.length > 0) {
          for (const token of articleTokens) {
            const { data: articleData } = await userOps.supabaseClient
              .from('library')
              .select('title, question')
              .eq('id', token)
              .single();

            if (articleData) {
              articlesMap.set(token, articleData as ArticleProfile);
            }
          }
        }

        // Calculate real metrics for homeworks
        if (homeworkData) {
          const enhancedHomeworks: EnhancedHomework[] = (
            homeworkData as unknown as HomeworkV2Profile[]
          ).map((hw) => {
            const submissionsForHw = (
              (allSubmissions as HomeworkStudentProfile[]) || []
            ).filter((s) => s.homework_token === hw.homework_token);

            const completedCount = submissionsForHw.filter(
              (s) => s.completed,
            ).length;
            const totalAssigned = submissionsForHw.length;

            // Calculate completion rate
            const completion_rate =
              totalAssigned > 0
                ? Math.round((completedCount / totalAssigned) * 100)
                : 0;

            // Calculate average score
            const completedSubmissions = submissionsForHw.filter(
              (s) => s.completed,
            );
            const scores = completedSubmissions
              .map((submission) => {
                const article = articlesMap.get(submission.article_token);
                return calculateScore(
                  submission.question_attempted,
                  article?.question || null,
                );
              })
              .filter((score) => score > 0);

            const average_score =
              scores.length > 0
                ? Math.round(
                    scores.reduce((sum, score) => sum + score, 0) /
                      scores.length,
                  )
                : 0;

            // Get word count from article
            const article = articlesMap.get(hw.article_token);
            const word_count = article?.article
              ? article.article.replace(/<[^>]*>/g, '').length
              : 0;

            return {
              ...hw,
              completion_rate,
              average_score,
              submissions_count: totalAssigned,
              status: hw.deleted ? ('archived' as const) : ('active' as const),
              word_count,
              content_id: hw.article_token,
              homework_id: hw.homework_token,
            };
          });
          setHomeworks(enhancedHomeworks);
        }

        // Load students with real analytics
        const { data: studentData } = await studentOps.getStudentsByClassId(
          classId,
        );
        if (studentData) {
          const totalHomeworks = homeworkData?.length || 0;

          const enhancedStudents: ClassStudent[] = (
            studentData as StudentProfile[]
          ).map((student) => {
            const studentSubmissions = (
              (allSubmissions as HomeworkStudentProfile[]) || []
            ).filter((s) => s.student_token === student.student_token);

            const completedCount = studentSubmissions.filter(
              (s) => s.completed,
            ).length;

            // Calculate completion rate
            const completion_rate =
              totalHomeworks > 0
                ? Math.round((completedCount / totalHomeworks) * 100)
                : 0;

            // Calculate average score
            const completedSubmissions = studentSubmissions.filter(
              (s) => s.completed,
            );
            const scores = completedSubmissions
              .map((submission) => {
                const article = articlesMap.get(submission.article_token);
                return calculateScore(
                  submission.question_attempted,
                  article?.question || null,
                );
              })
              .filter((score) => score > 0);

            const average_score =
              scores.length > 0
                ? Math.round(
                    scores.reduce((sum, score) => sum + score, 0) /
                      scores.length,
                  )
                : 0;

            // Get last activity from completed_at dates
            const lastActivityDate = studentSubmissions
              .filter((s) => s.completed_at)
              .map((s) => new Date(s.completed_at).getTime())
              .sort((a, b) => b - a)[0];

            const last_activity = lastActivityDate
              ? new Date(lastActivityDate).toISOString()
              : undefined;

            return {
              ...student,
              completion_rate,
              average_score,
              assignments_completed: completedCount,
              last_activity,
            };
          });
          setStudents(enhancedStudents);

          // Calculate class average stats
          const totalStudents = studentData?.length || 0;
          const activeHomeworks =
            (homeworkData as unknown as HomeworkV2Profile[])?.filter(
              (hw) => !hw.deleted,
            ).length || 0;

          const avgCompletion =
            enhancedStudents.length > 0
              ? enhancedStudents.reduce(
                  (sum, s) => sum + (s.completion_rate || 0),
                  0,
                ) / enhancedStudents.length
              : 0;

          setStats((prev) => ({
            ...prev,
            totalStudents,
            totalHomeworks,
            activeHomeworks,
            averageCompletion: avgCompletion,
          }));
        }
      } catch (error: any) {
        console.error('Error loading class data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (classId) {
      loadClassData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]); // Only depend on classId to prevent infinite loops

  // Event handlers
  const handleViewHomework = (homework: EnhancedHomework) => {
    navigate(`/teacher/content/${homework.content_id}/view`);
  };

  const handleEditHomework = (homework: EnhancedHomework) => {
    navigate(`/admin/library/${homework.content_id}/edit`);
  };

  const handleRecallHomework = (homework: EnhancedHomework) => {
    setRecallHomework(homework);
    setIsRecallOpen(true);
  };

  const handleHomeworkAnalytics = (homework: EnhancedHomework) => {
    // Use homework_token which is the actual identifier in the database
    const homeworkId = homework.homework_token || homework.homework_id;
    navigate(`/teacher/${year}/class/${classId}/analytics/${homeworkId}`);
  };

  const handleStudentDetails = (student: ClassStudent) => {
    navigate(`/admin/user/${student.student_token}`);
  };

  // Table columns for homeworks
  const homeworkColumns: TableColumn<EnhancedHomework>[] = [
    {
      key: 'title',
      header: 'Assignment',
      render: (homework: EnhancedHomework, value: string) => (
        <VStack spacing={1} align="start">
          <Text fontSize="sm" fontWeight="600" noOfLines={1}>
            {value}
          </Text>
        </VStack>
      ),
    },
    {
      key: 'created_at',
      header: 'Assigned',
      render: (homework: EnhancedHomework, value: string) => (
        <Text fontSize="sm" color={muted} whiteSpace="nowrap">
          {value ? new Date(value).toLocaleDateString() : '—'}
        </Text>
      ),
    },
    {
      key: 'completion_rate',
      header: 'Progress',
      render: (homework: EnhancedHomework, value: number) => (
        <VStack spacing={1} align="stretch" minW="50px">
          <HStack justify="space-between">
            <Text fontSize="sm" fontWeight="600">
              {value || 0}%
            </Text>
            <Text fontSize="xs" color={muted}>
              {homework.submissions_count || 0} assigned
            </Text>
          </HStack>
          <Progress
            value={value || 0}
            size="xs"
            colorScheme="blue"
            borderRadius="full"
            aria-label={`${homework.title} completion`}
          />
        </VStack>
      ),
    },
    {
      key: 'average_score',
      header: 'Avg Score',
      render: (homework: EnhancedHomework, value: number) => (
        <VStack spacing={1} align="center">
          <Text
            fontSize="sm"
            fontWeight="600"
            color={
              value >= 80 ? 'green.600' : value >= 60 ? 'yellow.600' : 'red.600'
            }
          >
            {value}%
          </Text>
        </VStack>
      ),
    },
  ];

  // Table columns for students
  const studentColumns: TableColumn<ClassStudent>[] = [
    {
      key: 'first_name',
      header: 'Student',
      render: (student: ClassStudent, value: string) => (
        <HStack spacing={3}>
          <Avatar
            size="sm"
            name={`${student.first_name} ${student.last_name}`}
            bg="blue.500"
          />
          <VStack spacing={1} align="start">
            <Text fontSize="sm" fontWeight="600">
              {student.first_name} {student.last_name}
            </Text>
            <Text fontSize="xs" color={muted}>
              {student.email}
            </Text>
          </VStack>
        </HStack>
      ),
    },
    {
      key: 'assignments_completed',
      header: 'Completed',
      render: (student: ClassStudent, value: number) => (
        <VStack spacing={0} align="center">
          <Text fontSize="sm" fontWeight="600">
            {value} / {stats.totalHomeworks}
          </Text>
          <Text fontSize="xs" color={muted}>
            ({student.completion_rate || 0}%)
          </Text>
        </VStack>
      ),
    },
    {
      key: 'average_score',
      header: 'Avg Score',
      render: (student: ClassStudent, value: number) => (
        <Badge
          colorScheme={value >= 80 ? 'green' : value >= 60 ? 'yellow' : 'red'}
          variant="subtle"
        >
          {value}%
        </Badge>
      ),
    },
    {
      key: 'last_activity',
      header: 'Last Activity',
      render: (student: ClassStudent, value: string) => {
        if (!value)
          return (
            <Text fontSize="xs" color={muted}>
              No activity yet
            </Text>
          );
        const daysAgo = Math.floor(
          (Date.now() - new Date(value).getTime()) / (1000 * 60 * 60 * 24),
        );
        return (
          <Text fontSize="xs" color={muted}>
            {daysAgo === 0
              ? 'Today'
              : daysAgo === 1
              ? 'Yesterday'
              : `${daysAgo} days ago`}
          </Text>
        );
      },
    },
  ];

  const filteredHomeworks = homeworks.filter((homework) =>
    (homework.title || '')
      .toLowerCase()
      .includes(assignmentQuery.trim().toLowerCase()),
  );
  const filteredStudents = students.filter((student) =>
    `${student.first_name} ${student.last_name} ${student.email || ''}`
      .toLowerCase()
      .includes(studentQuery.trim().toLowerCase()),
  );
  const recentStudents = students
    .filter((student) => student.last_activity)
    .sort(
      (a, b) =>
        new Date(b.last_activity!).getTime() -
        new Date(a.last_activity!).getTime(),
    )
    .slice(0, 5);

  return (
    <AdminLayout padding={3}>
      <VStack spacing={4} align="stretch">
        <Box>
          <Breadcrumb
            items={[
              {
                label: isAdminView ? 'Admin' : 'Teacher',
                href: isAdminView ? '/admin' : '/teacher',
              },
              {
                label: 'Classes',
                href: isAdminView ? `/admin/${year}/class` : '/teacher',
              },
              { label: stats.className || 'Class dashboard', isCurrent: true },
            ]}
          />
          <Flex
            justify="space-between"
            align="center"
            gap={3}
            wrap="wrap"
            mt={3}
          >
            <Box>
              <Heading size="md">
                {stats.className || 'Class'} dashboard
              </Heading>
              <Text fontSize="sm" color={muted} mt={1}>
                {[
                  stats.semesterYear || year,
                  stats.grade && `Grade ${stats.grade}`,
                  stats.teacherName,
                ]
                  .filter(Boolean)
                  .join(' • ')}
              </Text>
            </Box>
            {isTeacherView && (
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FiPlus />}
                onClick={() =>
                  navigate(`/teacher/${year}/class/${classId}/homeworks`)
                }
              >
                Assign homework
              </Button>
            )}
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          {[
            {
              label: 'Students',
              value: stats.totalStudents,
              detail: 'Enrolled in class',
              icon: FiUsers,
            },
            {
              label: 'Assignments',
              value: stats.totalHomeworks,
              detail: 'Total assigned',
              icon: FiBookOpen,
            },
            {
              label: 'Active assignments',
              value: stats.activeHomeworks,
              detail: 'Currently published',
              icon: FiClock,
            },
            {
              label: 'Completion',
              value: `${stats.averageCompletion.toFixed(1)}%`,
              detail: 'Class average',
              icon: FiTarget,
            },
          ].map(({ label, value, detail, icon: Icon }) => (
            <Box
              key={label}
              bg={surface}
              borderWidth="1px"
              borderColor={border}
              borderRadius="lg"
              p={4}
            >
              <HStack justify="space-between" color={muted} mb={2}>
                <Text fontSize="sm">{label}</Text>
                <Icon aria-hidden="true" />
              </HStack>
              <Skeleton isLoaded={!isLoading}>
                <Text fontSize="2xl" fontWeight="bold" lineHeight="short">
                  {value}
                </Text>
              </Skeleton>
              <Text fontSize="xs" color={muted} mt={1}>
                {detail}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <Box
          bg={surface}
          borderWidth="1px"
          borderColor={border}
          borderRadius="lg"
          p={{ base: 3, md: 4 }}
          minW={0}
        >
          <Tabs
            index={activeTab}
            onChange={setActiveTab}
            colorScheme="blue"
            isLazy
          >
            <TabList overflowX="auto">
              {[
                {
                  label: 'Assignments',
                  count: homeworks.length,
                  icon: FiBookOpen,
                },
                { label: 'Students', count: students.length, icon: FiUsers },
                { label: 'Overview', icon: FiBarChart },
              ].map(({ label, count, icon: Icon }) => (
                <Tab
                  key={label}
                  fontSize="sm"
                  fontWeight="600"
                  px={{ base: 3, md: 4 }}
                  gap={2}
                  flexShrink={0}
                >
                  <Box
                    as={Icon}
                    display={{ base: 'none', sm: 'block' }}
                    aria-hidden="true"
                  />
                  {label}
                  {count !== undefined && (
                    <Badge borderRadius="full" px={2}>
                      {isLoading ? '—' : count}
                    </Badge>
                  )}
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {/* Assignments Tab */}
              <TabPanel px={0} pb={0} pt={4}>
                <VStack spacing={3} align="stretch">
                  <Stack
                    direction={{ base: 'column', md: 'row' }}
                    justify="space-between"
                    spacing={3}
                  >
                    <InputGroup size="sm" maxW={{ md: '300px' }}>
                      <InputLeftElement pointerEvents="none">
                        <FiSearch />
                      </InputLeftElement>
                      <Input
                        aria-label="Search assignments"
                        placeholder="Search assignments"
                        value={assignmentQuery}
                        onChange={(event) =>
                          setAssignmentQuery(event.target.value)
                        }
                        borderRadius="md"
                      />
                    </InputGroup>
                  </Stack>
                  <Text fontSize="xs" color={muted} role="status">
                    {isLoading
                      ? 'Loading assignments…'
                      : `${filteredHomeworks.length} of ${homeworks.length} assignments`}
                  </Text>

                  <DataTable
                    compact
                    size="sm"
                    striped={false}
                    data={filteredHomeworks}
                    columns={homeworkColumns}
                    loading={isLoading}
                    clickableRows={true}
                    onRowClick={handleViewHomework}
                    actions={
                      isTeacherView || isAdminView
                        ? [
                            {
                              label: 'View',
                              icon: <FiEye />,
                              onClick: handleViewHomework,
                              variant: 'secondary',
                            },
                            {
                              label: 'Analytics',
                              icon: <FiBarChart />,
                              onClick: handleHomeworkAnalytics,
                              variant: 'primary',
                            },
                            ...(isTeacherView
                              ? [
                                  {
                                    label: 'Recall',
                                    icon: <FiRotateCcw />,
                                    onClick: handleRecallHomework,
                                    variant: 'warning' as const,
                                  },
                                ]
                              : []),
                            ...(isAdminView
                              ? [
                                  {
                                    label: 'Edit',
                                    icon: <FiEdit />,
                                    onClick: handleEditHomework,
                                    variant: 'secondary' as const,
                                  },
                                ]
                              : []),
                          ]
                        : undefined
                    }
                    emptyState={
                      <VStack
                        spacing={2}
                        py={8}
                        textAlign="center"
                        color={muted}
                      >
                        <FiBookOpen size={28} aria-hidden="true" />
                        <Text fontWeight="600">
                          {assignmentQuery.trim()
                            ? 'No matching assignments'
                            : 'No assignments yet'}
                        </Text>
                        <Text fontSize="sm">
                          {assignmentQuery.trim()
                            ? 'Try a different search.'
                            : 'Published assignments will appear here.'}
                        </Text>
                        {assignmentQuery && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setAssignmentQuery('')}
                          >
                            Clear search
                          </Button>
                        )}
                      </VStack>
                    }
                  />
                </VStack>
              </TabPanel>

              {/* Students Tab */}
              <TabPanel px={0} pb={0} pt={4}>
                <VStack spacing={3} align="stretch">
                  <Stack
                    direction={{ base: 'column', md: 'row' }}
                    justify="space-between"
                    spacing={3}
                  >
                    <InputGroup size="sm" maxW={{ md: '300px' }}>
                      <InputLeftElement pointerEvents="none">
                        <FiSearch />
                      </InputLeftElement>
                      <Input
                        aria-label="Search students"
                        placeholder="Search name or email"
                        value={studentQuery}
                        onChange={(event) =>
                          setStudentQuery(event.target.value)
                        }
                        borderRadius="md"
                      />
                    </InputGroup>
                  </Stack>
                  <Text fontSize="xs" color={muted} role="status">
                    {isLoading
                      ? 'Loading students…'
                      : `${filteredStudents.length} of ${students.length} students`}
                  </Text>

                  <DataTable
                    compact
                    size="sm"
                    striped={false}
                    data={filteredStudents}
                    columns={studentColumns}
                    loading={isLoading}
                    clickableRows={isAdminView}
                    onRowClick={isAdminView ? handleStudentDetails : undefined}
                    actions={
                      isAdminView
                        ? [
                            {
                              label: 'View Details',
                              icon: <FiEye />,
                              onClick: handleStudentDetails,
                              variant: 'secondary',
                            },
                          ]
                        : undefined
                    }
                    emptyState={
                      <VStack
                        spacing={2}
                        py={8}
                        textAlign="center"
                        color={muted}
                      >
                        <FiUsers size={28} aria-hidden="true" />
                        <Text fontWeight="600">
                          {studentQuery.trim()
                            ? 'No matching students'
                            : 'No students enrolled'}
                        </Text>
                        <Text fontSize="sm">
                          {studentQuery.trim()
                            ? 'Try a different search.'
                            : 'Students will appear here once enrolled in this class.'}
                        </Text>
                        {studentQuery && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setStudentQuery('')}
                          >
                            Clear search
                          </Button>
                        )}
                      </VStack>
                    }
                  />
                </VStack>
              </TabPanel>

              {/* Overview Tab */}
              <TabPanel px={0} pb={0} pt={4}>
                <VStack spacing={4} align="stretch">
                  <Text fontSize="md" fontWeight="600">
                    Class Overview
                  </Text>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {/* Completion distribution */}
                    <Card padding={4} variant="outline">
                      <VStack spacing={4} align="stretch">
                        <HStack>
                          <FiTarget />
                          <Text fontSize="md" fontWeight="600">
                            Completion distribution
                          </Text>
                        </HStack>
                        {[
                          {
                            label: 'Excellent (90%+)',
                            count: students.filter(
                              (s) => (s.completion_rate || 0) >= 90,
                            ).length,
                            color: 'green',
                          },
                          {
                            label: 'Good (70-89%)',
                            count: students.filter(
                              (s) =>
                                (s.completion_rate || 0) >= 70 &&
                                (s.completion_rate || 0) < 90,
                            ).length,
                            color: 'blue',
                          },
                          {
                            label: 'Fair (50-69%)',
                            count: students.filter(
                              (s) =>
                                (s.completion_rate || 0) >= 50 &&
                                (s.completion_rate || 0) < 70,
                            ).length,
                            color: 'yellow',
                          },
                          {
                            label: 'Needs Help (<50%)',
                            count: students.filter(
                              (s) => (s.completion_rate || 0) < 50,
                            ).length,
                            color: 'red',
                          },
                        ].map(({ label, count, color }) => {
                          const percentage =
                            students.length > 0
                              ? (count / students.length) * 100
                              : 0;
                          return (
                            <HStack key={label} justify="space-between">
                              <Text fontSize="sm">{label}</Text>
                              <HStack>
                                <Text fontSize="sm" fontWeight="600">
                                  {count}
                                </Text>
                                <Progress
                                  value={percentage}
                                  size="sm"
                                  colorScheme={color as any}
                                  minW="60px"
                                />
                              </HStack>
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Card>

                    {/* Recent activity */}
                    <Card padding={4} variant="outline">
                      <VStack spacing={4} align="stretch">
                        <HStack>
                          <FiClock />
                          <Text fontSize="md" fontWeight="600">
                            Recent activity
                          </Text>
                        </HStack>
                        <VStack spacing={3} align="stretch">
                          {recentStudents.map((student) => (
                            <HStack
                              key={student.student_token}
                              justify="space-between"
                              p={2}
                              bg={subtle}
                              borderRadius="md"
                            >
                              <HStack>
                                <Avatar
                                  size="xs"
                                  name={`${student.first_name} ${student.last_name}`}
                                />
                                <Text fontSize="sm">
                                  {student.first_name} {student.last_name}
                                </Text>
                              </HStack>
                              <Text fontSize="xs" color={muted}>
                                {student.last_activity &&
                                  new Date(
                                    student.last_activity,
                                  ).toLocaleDateString()}
                              </Text>
                            </HStack>
                          ))}
                          {recentStudents.length === 0 && (
                            <Text
                              fontSize="sm"
                              color={muted}
                              textAlign="center"
                            >
                              No recent activity
                            </Text>
                          )}
                        </VStack>
                      </VStack>
                    </Card>
                  </SimpleGrid>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
      <UnpublishModal
        isOpen={isRecallOpen}
        onClose={() => setIsRecallOpen(false)}
        onUnpublishClick={onRecallClick}
        title={recallHomework?.title ?? 'Untitled'}
      />
    </AdminLayout>
  );
};
